/**
 * Provider-neutral FlightDetailsViewModel enrichment from offer / price VMs.
 * No rawDeal. Missing evidence → null.
 */

/**
 * @param {{
 *   offerVm: import("../view-models/types").FlightOfferViewModel|null,
 *   priceVm?: import("../view-models/types").BookingPriceViewModel|null,
 * }} input
 * @returns {object|null}
 */
export function buildFlightDetailsViewModel(input = {}) {
  const offerVm = input.offerVm;
  if (!offerVm?.id) return null;

  const segments = Array.isArray(offerVm.segments) ? offerVm.segments : [];
  const outboundSegs = segments.filter(
    (s) => !s.direction || s.direction === "outbound" || s.direction === "out"
  );
  const inboundSegs = segments.filter(
    (s) => s.direction === "inbound" || s.direction === "in" || s.direction === "return"
  );

  // If directions absent, treat first as outbound and second as inbound.
  const outList =
    outboundSegs.length > 0
      ? outboundSegs
      : segments.length > 0
        ? [segments[0]]
        : [];
  const inList =
    inboundSegs.length > 0
      ? inboundSegs
      : segments.length > 1
        ? [segments[1]]
        : [];

  const airlineCode =
    str(outList[0]?.airlineCode) ||
    str(offerVm.cardMeta?.airlineCode) ||
    null;
  const airlineName =
    str(offerVm.airlineSummary) ||
    str(offerVm.cardMeta?.airlineName) ||
    airlineCode;

  const fare = offerVm.fare || {};
  const conditions = {
    changePolicy: str(fare.changePolicy),
    cancelPolicy: str(fare.cancelPolicy),
    refundability: str(fare.refundability),
    ticketingDeadline: str(fare.ticketingDeadline),
  };

  const fareBadges = [];
  if (fare.charter === true) fareBadges.push("CHARTER");
  else if (fare.fareType) fareBadges.push(String(fare.fareType));

  return {
    id: String(offerVm.id),
    airline: airlineCode
      ? {
          code: airlineCode,
          name: airlineName || airlineCode,
          logoText: airlineCode,
        }
      : null,
    fareBadges,
    outbound: buildLegPresentation(outList, "טיסת הלוך"),
    inbound: inList.length ? buildLegPresentation(inList, "טיסת חזור") : null,
    fare: {
      cabin: str(fare.cabin),
      fareFamily: str(fare.fareFamily),
      fareType: str(fare.fareType),
    },
    baggage: offerVm.baggage || [],
    conditions,
    alerts: [],
    capabilities: offerVm.capabilities || null,
  };
}

/**
 * @param {import("../view-models/types").FlightSegmentViewModel[]} segs
 * @param {string} title
 */
function buildLegPresentation(segs, title) {
  if (!segs.length) return null;
  const first = segs[0];
  const last = segs[segs.length - 1];
  const stopCount =
    segs.length > 1
      ? segs.length - 1
      : finite(first.stopCount) ??
        (first.stopsLabel && /ישיר/u.test(first.stopsLabel) ? 0 : null) ??
        0;
  const durationMinutes = segs.reduce((acc, s) => {
    const d = finite(s.durationMinutes);
    return d != null ? acc + d : acc;
  }, 0);
  const durationLabel =
    (durationMinutes > 0 ? formatDurationMinutes(durationMinutes) : null) ||
    str(first.durationLabel) ||
    null;

  const segmentsPresentation =
    segs.length > 1
      ? segs.map((s) => ({
          type: "segment",
          flightNumber: str(s.flightNumber),
          operatingCarrier: str(s.operatingCarrier),
          cabin: str(s.cabin),
          depIata: str(s.originCode || s.departureAirport),
          arrIata: str(s.destinationCode || s.arrivalAirport),
          depTime: str(s.departureTime),
          arrTime: str(s.arrivalTime),
          duration:
            formatDurationMinutes(s.durationMinutes) || str(s.durationLabel),
          operatedBy: str(s.operatingCarrier),
          aircraft: str(s.aircraft),
        }))
      : [];

  return {
    title,
    weekdayLabel: null,
    departure: {
      airport: str(first.originCode || first.departureAirport),
      time: str(first.departureTime),
    },
    arrival: {
      airport: str(last.destinationCode || last.arrivalAirport),
      time: str(last.arrivalTime),
    },
    duration: durationLabel,
    stops: stopCount,
    stopsLabel:
      str(first.stopsLabel) ||
      (stopCount === 0
        ? "ישיר"
        : stopCount === 1
          ? "עצירה אחת"
          : `${stopCount} עצירות`),
    segments: segmentsPresentation,
    depTime: str(first.departureTime),
    depIata: str(first.originCode || first.departureAirport),
    arrTime: str(last.arrivalTime),
    arrIata: str(last.destinationCode || last.arrivalAirport),
    flightNumber: str(first.flightNumber),
    operatedBy: str(first.operatingCarrier),
    cabin: str(first.cabin),
    aircraft: str(first.aircraft),
    date: str(first.departureDate) || null,
  };
}

function formatDurationMinutes(mins) {
  const n = Number(mins);
  if (!Number.isFinite(n) || n < 0) return null;
  const h = Math.floor(n / 60);
  const m = Math.round(n % 60);
  return `${h}:${String(m).padStart(2, "0")}`;
}

function finite(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function str(v) {
  if (v == null) return null;
  const s = String(v).trim();
  return s || null;
}
